import { FluidThemePalette } from '@lmig/fluid-core';
import { FunctionalComponent, h } from '@stencil/core';

import { FluidFunctionalComponentProps } from '../../../model/functional/fluid-functional-component-props.interface';
import { themeVar } from '../../../utils/themes/theme.variables';
import { Utils } from '../../../utils/utils';
import { FluidHeader } from '../fluid-header';

export interface HeaderProps extends FluidFunctionalComponentProps {
  host?: FluidHeader;
  setRef?: (el: HTMLElement) => void;
}

// ================================================================ //
// -- Public Functional Components

/**
 * Renders toggle button that opens/closes mobile "flyout" menu.
 */
export const HeaderMobileBtn: FunctionalComponent<HeaderProps> = (
  props: HeaderProps
) => {
  const { host } = props;
  const hasContent =
    !!host.links?.length ||
    !!host.host.querySelector('[slot="header-content"]') ||
    !!host.host.querySelector('[slot="header-search"]');

  return (
    hasContent && (
      <button
        title={!host._mobileMenuOpen ? 'Open Menu' : 'Close Menu'}
        class='header-mobile-toggle'
        onClick={() => host.toggleMobileMenu()}>
        <span
          role='presentation'
          class={{
            'toggle-icon-animation': true,
            dismiss: host._mobileMenuOpen,
          }}
        />
      </button>
    )
  );
};

/**
 * Renders Header logo, FLUID logo, or slot for custom logo. Optionally
 * renders FLUID Link wrapper for logo if "logoConfig" Prop provided.
 * Since Header's logo changes position in the DOM based on viewport
 * size, this functional component is called twice for "mobile" &
 * "desktop" positions, & will short-circuit if the viewport size
 * doesn't match the position being rendered.
 */
export const HeaderLogo: FunctionalComponent<
  HeaderProps & { position: 'mobile' | 'desktop' }
> = ({ host, position }) => {
  const { logoConfig, noLogo, fluidLogo, theme, _mobileViewport } = host;

  if (
    noLogo ||
    (_mobileViewport && position === 'desktop') ||
    (!_mobileViewport && position === 'mobile')
  )
    return;

  const hasSlottedLogo = Utils.hasSlotContent(host.host, 'header-logo');
  // If "href" or "route" properties exist in "logoConfig", render a FLUID Link
  const LogoContainerTag =
    !!logoConfig?.href || !!logoConfig?.route ? 'fluid-link' : 'div';
  // If rendering FLUID Link, extract "tooltip" property & rename as "title"
  const { tooltip: title = null, ...linkConfig } =
    LogoContainerTag === 'fluid-link' ? logoConfig : {};
  // Only create Props object if FLUID Link is being rendered
  const linkProps = !Utils.isEmptyObject(linkConfig) && {
    ...linkConfig,
    title,
    theme,
    overrideGlobalTheme: true,
  };

  return (
    <LogoContainerTag class='header-logo' {...linkProps}>
      <span
        innerHTML={!hasSlottedLogo ? getLogo(host) : null}
        class={{
          'header-logo-icon': true,
          'fluid-logo': fluidLogo,
          'slotted-logo': hasSlottedLogo,
        }}>
        {hasSlottedLogo && <slot name='header-logo' />}
      </span>
    </LogoContainerTag>
  );
};

/**
 * Conditionally renders slot for "search bar". Only renders if
 * child element(s) with the appropriate "slot" attribute exists.
 */
export const HeaderSearch: FunctionalComponent<HeaderProps> = (
  props: HeaderProps
) => {
  const { host } = props;
  const hasSlottedContent = Utils.hasSlotContent(host.host, 'header-search');

  return (
    hasSlottedContent && (
      <div key='header-search' class='header-search-slot'>
        <slot name='header-search' />
      </div>
    )
  );
};

/**
 * Renders slot for additional Header content.
 */
export const HeaderContent: FunctionalComponent<HeaderProps> = (
  props: HeaderProps
) => {
  const { setRef } = props;

  return (
    <div
      key='header-content'
      ref={(el: HTMLElement) => setRef(el)}
      class='header-content-slot'>
      <slot name='header-content' />
    </div>
  );
};

/**
 * If "fixedPosition" Prop is true, renders a "placeholder" element
 * with height dynamically matched to the height of the main Header
 * container (compensates for Header's "position: fixed" that removes
 * Header from the standard DOM flow).
 */
export const HeaderPlaceholder: FunctionalComponent<HeaderProps> = (
  props: HeaderProps & { containerHeight: number }
) => {
  const { host, containerHeight, setRef } = props;

  return (
    host.fixedPosition && (
      <div
        ref={(el: HTMLElement) => setRef(el)}
        style={{ height: `${containerHeight}px` }}
        class='header-placeholder'
      />
    )
  );
};

// ================================================================ //
// -- Functional Component Utilities (Private)

const getLogo = (host: FluidHeader): string => {
  switch (true) {
    case host.fluidLogo:
      return themeVar<string>(host.theme, 'fluidLogo');
    case host.themePalette === FluidThemePalette.HELMSMAN:
      return themeVar<string>(host.theme, 'helmsmanLogo');
    default:
      return themeVar<string>(host.theme, 'logo');
  }
};
